import { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { X, AlertTriangle, ArrowRight } from 'lucide-react'

/**
 * Shows a modal when user tries to leave the devis flow.
 * Only triggers on desktop (mouse leaves viewport top).
 * Shows once per session.
 *
 * SEO/UX guardrails:
 *  - NEVER arms on step 1 (SERP arrival page — Google penalizes interstitials
 *    that fire on first-paint/early-interaction).
 *  - Even on step 2+, requires meaningful engagement before arming:
 *    (a) 60s dwell on the page, OR
 *    (b) user has scrolled past 50% of page height at least twice.
 *  - Once-per-session via sessionStorage.
 */
export function ExitIntentModal({ currentStep, totalSteps = 3 }: { currentStep: number; totalSteps?: number }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Hard gate: never arm on step 1 (SERP entry) or out-of-range steps.
    if (currentStep < 2 || currentStep > totalSteps) return
    if (sessionStorage.getItem('exit-intent-shown')) return

    let armed = false
    let deepScrolls = 0
    let timer: ReturnType<typeof setTimeout> | null = null

    function arm() {
      if (armed) return
      armed = true
    }

    // (a) Time-based engagement: 60s dwell.
    timer = setTimeout(arm, 60_000)

    // (b) Scroll-based engagement: 2 deep scrolls past 50% of page.
    let pastHalf = false
    function handleScroll() {
      const doc = document.documentElement
      const scrolled = (window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight, 1)
      if (scrolled >= 0.5) {
        if (!pastHalf) {
          pastHalf = true
          deepScrolls += 1
          if (deepScrolls >= 2) arm()
        }
      } else {
        pastHalf = false
      }
    }

    function handleMouseLeave(e: MouseEvent) {
      if (!armed) return
      if (e.clientY < 5 && !visible) {
        setVisible(true)
        sessionStorage.setItem('exit-intent-shown', '1')
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [currentStep, totalSteps, visible])

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in"
    >
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <button
          type="button"
          onClick={() => setVisible(false)}
          aria-label="Fermer la fenêtre"
          className="absolute right-3 top-3 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
          <AlertTriangle className="h-6 w-6 text-amber-600" aria-hidden="true" />
        </div>

        <h3 id="exit-intent-title" className="mt-3 text-lg font-bold text-slate-900">Votre demande est presque terminée !</h3>
        <p className="mt-2 text-sm text-slate-500">
          Vous êtes à l'étape {currentStep} sur {totalSteps}. Terminez votre demande pour recevoir 3 devis gratuits sous 48h.
        </p>

        <div className="mt-5 space-y-2">
          <button
            type="button"
            onClick={() => setVisible(false)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press"
          >
            Continuer ma demande <ArrowRight className="h-4 w-4" />
          </button>
          <Link
            to="/"
            className="block text-center text-xs text-slate-400 hover:text-slate-600"
          >
            Quitter (mes réponses seront sauvegardées)
          </Link>
        </div>

        <div className="mt-4 flex items-center justify-center gap-3 text-[10px] text-slate-400">
          <span>100% gratuit</span>
          <span>·</span>
          <span>Sans engagement</span>
          <span>·</span>
          <span>3 devis en 48h</span>
        </div>
      </div>
    </div>
  )
}
